import { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { 
  LayoutDashboard, Users, Upload, Sparkles, 
  FileText, Settings, LogOut, Menu, X,
  ChevronDown, Bell, Shield, Target, BarChart3, Crown, Briefcase, UserCog, Link2, KeyRound, IndianRupee, Bot, MessageCircle, Phone, Calendar
} from 'lucide-react';
import ChangePasswordModal from './auth/ChangePasswordModal';
import { API_URL } from '../utils/apiUrl';

// Official Isprava Wealth Logo
const ISPRAVA_LOGO = "https://customer-assets.emergentagent.com/job_ai-finance-chat-1/artifacts/4qsgtdab_Isprava%20Logo%20final.JPG";

const Layout = () => {
  const { user, logout, hasRole, token } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [newMeetingsCount, setNewMeetingsCount] = useState(0);

  // Fetch pending approvals count for Principal/Business Head
  useEffect(() => {
    const fetchPendingApprovals = async () => {
      if (!token || !['principal', 'business_head'].includes(user?.role)) return;
      
      try {
        const res = await fetch(`${API_URL}/target-definition/approvals?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Count pending approvals
          const pending = Array.isArray(data) ? data.filter(a => a.status === 'pending').length : 0;
          setPendingApprovalsCount(pending);
        }
      } catch (err) {
        console.error('Error fetching pending approvals:', err);
      }
    };

    fetchPendingApprovals();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingApprovals, 30000);
    return () => clearInterval(interval);
  }, [token, user?.role]);

  // Fetch new meetings count for Principal/Business Head
  useEffect(() => {
    const fetchNewMeetings = async () => {
      if (!token || !['principal', 'business_head'].includes(user?.role)) return;
      
      try {
        const res = await fetch(`${API_URL}/target-definition/meetings/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setNewMeetingsCount(data.count || 0);
        }
      } catch (err) {
        console.error('Error fetching meetings count:', err);
      }
    };

    fetchNewMeetings();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNewMeetings, 30000);
    return () => clearInterval(interval);
  }, [token, user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { 
      path: '/dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      roles: ['principal', 'business_head', 'rm', 'dealer', 'backoffice']
    },
    { 
      path: '/clients', 
      icon: Users, 
      label: 'Clients',
      roles: ['principal', 'business_head', 'rm', 'dealer', 'backoffice']
    },
    { 
      path: '/targets', 
      icon: Target, 
      label: 'Targets',
      roles: ['principal', 'business_head', 'rm', 'dealer']
    },
    { 
      path: '/my-targets', 
      icon: Target, 
      label: 'My Targets',
      roles: ['rm', 'dealer']
    },
    { 
      path: '/target-definition', 
      icon: Settings, 
      label: 'Target Definition',
      roles: ['principal', 'business_head']
    },
    { 
      path: '/market-holidays', 
      icon: Calendar, 
      label: 'Market Holidays',
      roles: ['principal', 'business_head']
    },
    { 
      path: '/shortfall-overview', 
      icon: BarChart3, 
      label: 'Shortfall Overview',
      roles: ['principal', 'business_head']
    },
    { 
      path: '/team-performance', 
      icon: BarChart3, 
      label: 'Team Performance',
      roles: ['rm']
    },
    { 
      path: '/my-dealers', 
      icon: Briefcase, 
      label: 'My Dealers',
      roles: ['rm']
    },
    { 
      path: '/approvals', 
      icon: Shield, 
      label: 'Approvals',
      roles: ['principal', 'business_head']
    },
    { 
      path: '/meeting-log', 
      icon: FileText, 
      label: 'Meeting Log',
      roles: ['principal', 'business_head', 'rm', 'dealer']
    },
    { 
      path: '/my-incentive', 
      icon: IndianRupee, 
      label: 'My Incentive',
      roles: ['rm', 'dealer']
    },
    { 
      path: '/performance', 
      icon: BarChart3, 
      label: 'Performance',
      roles: ['principal', 'business_head', 'rm', 'dealer']
    },
    { 
      path: '/import', 
      icon: Upload, 
      label: 'Data Import',
      roles: ['principal', 'business_head', 'backoffice'],
      allowedEmails: ['kiran@ispravawealth.com']  // Special access for specific users
    },
    { 
      path: '/data-fetch', 
      icon: Bot, 
      label: 'Data Fetch',
      roles: ['principal', 'business_head', 'backoffice']
    },
    { 
      path: '/whatsapp', 
      icon: MessageCircle, 
      label: 'WhatsApp',
      roles: ['principal']
    },
    { 
      path: '/call-history', 
      icon: Phone, 
      label: 'Call History',
      roles: ['principal', 'business_head', 'rm', 'dealer']
    },
    { 
      path: '/analytics', 
      icon: Sparkles, 
      label: 'AI Analytics',
      roles: ['principal', 'business_head', 'rm', 'dealer']
    },
    { 
      path: '/audit', 
      icon: FileText, 
      label: 'Audit Logs',
      roles: ['principal']
    },
    { 
      path: '/tier-config', 
      icon: Crown, 
      label: 'Client Tiers',
      roles: ['principal']
    },
    { 
      path: '/incentives', 
      icon: IndianRupee, 
      label: 'Incentives',
      roles: ['principal', 'business_head']
    },
    { 
      path: '/staff', 
      icon: UserCog, 
      label: 'Staff Management',
      roles: ['principal']
    },
  ];

  const filteredNavItems = navItems.filter(item => {
    // Check if user has required role
    if (hasRole(item.roles)) return true;
    
    // Check if user's email is in allowedEmails list
    if (item.allowedEmails && user?.email && item.allowedEmails.includes(user.email)) {
      return true;
    }
    
    return false;
  });

  const roleLabels = {
    principal: { label: 'Principal', color: 'text-[#C5A059]' },
    business_head: { label: 'Business Head', color: 'text-sky-500' },
    rm: { label: 'Relationship Manager', color: 'text-purple-500' },
    dealer: { label: 'Dealer', color: 'text-emerald-500' },
    backoffice: { label: 'Back Office', color: 'text-violet-500' },
    client: { label: 'Client', color: 'text-slate-400' }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  return (
    <div className="min-h-screen bg-[#020617] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 sidebar fixed inset-y-0 left-0 z-50">
        {/* Logo */}
        <div className="py-4 px-3 border-b border-white/5 flex items-center justify-center">
          <Link to="/dashboard" className="block hover:opacity-90 transition-opacity" data-testid="logo-link">
            <img 
              src={ISPRAVA_LOGO} 
              alt="Isprava Wealth" 
              className="w-full h-auto max-h-24"
              style={{ 
                maxWidth: '220px',
                objectFit: 'contain'
              }}
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive ? 'active text-white' : 'text-slate-400 hover:text-white'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.label}</span>
              {item.path === '/approvals' && pendingApprovalsCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                  {pendingApprovalsCount}
                </span>
              )}
              {item.path === '/meeting-log' && newMeetingsCount > 0 && (
                <span className="ml-auto bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                  {newMeetingsCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-10 h-10 bg-white/10">
              <AvatarFallback className="bg-[#C5A059] text-[#020617] font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className={`text-xs ${roleLabels[user?.role]?.color || 'text-slate-400'}`}>
                {roleLabels[user?.role]?.label || user?.role}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <div className={`lg:hidden fixed inset-0 z-50 ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
        <aside className="fixed inset-y-0 left-0 w-64 sidebar animate-slide-in">
          {/* Logo */}
          <div className="py-3 px-3 flex items-center justify-between border-b border-white/5">
            <Link to="/dashboard" className="flex-1 hover:opacity-90 transition-opacity">
              <img 
                src={ISPRAVA_LOGO} 
                alt="Isprava Wealth" 
                className="w-full h-auto max-h-16"
                style={{ 
                  maxWidth: '160px',
                  objectFit: 'contain'
                }}
              />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5 text-slate-400" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `sidebar-item flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive ? 'active text-white' : 'text-slate-400 hover:text-white'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.path === '/approvals' && pendingApprovalsCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                    {pendingApprovalsCount}
                  </span>
                )}
                {item.path === '/meeting-log' && newMeetingsCount > 0 && (
                  <span className="ml-auto bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                    {newMeetingsCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:pl-64">
        {/* Top Header */}
        <header className="h-16 glass sticky top-0 z-40 flex items-center justify-between px-6">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5 text-slate-400" />
          </Button>

          {/* Desktop - Breadcrumb area */}
          <div className="hidden lg:flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-500" />
            <span className="text-sm text-slate-400">SEBI Registered AP</span>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5 text-slate-400" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#C5A059] rounded-full" />
            </Button>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 hover:bg-white/5">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-[#C5A059] text-[#020617] text-sm font-semibold">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block text-sm text-white">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#0F172A] border-white/10">
                <DropdownMenuLabel>
                  <div>
                    <p className="text-white">{user?.name}</p>
                    <p className="text-xs text-slate-400">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/5" />
                <DropdownMenuItem 
                  className="text-slate-300 focus:bg-white/5 focus:text-white cursor-pointer"
                  onClick={() => navigate('/profile')}
                  data-testid="profile-settings-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Profile & Phone Numbers
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-slate-300 focus:bg-white/5 focus:text-white cursor-pointer"
                  onClick={() => setShowChangePassword(true)}
                  data-testid="change-password-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/5" />
                <DropdownMenuItem 
                  className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        token={token}
      />
    </div>
  );
};

export default Layout;
